import React from 'react'
import { RichTextEditor as Editor } from '@mantine/tiptap'
import type { StarterKitOptions } from '@tiptap/starter-kit'

import type { RichTextEditorControl } from './rich-text-editor.types'

export const TOOLBAR_GROUPS: RichTextEditorControl[][] = [
  [
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'clearFormatting',
    'highlight',
  ],
  ['h1', 'h2', 'h3'],
  ['blockquote', 'hr', 'bulletList', 'orderedList'],
  ['link', 'unlink'],
  ['undo', 'redo'],
]

export function isControlAllowed(
  controls: RichTextEditorControl[] | undefined
): (c: RichTextEditorControl) => boolean {
  return (c) => controls === undefined || controls.includes(c)
}

export function getVisibleToolbarGroups(
  controls: RichTextEditorControl[] | undefined
): RichTextEditorControl[][] {
  const hasControl = isControlAllowed(controls)
  return TOOLBAR_GROUPS.map((group) => group.filter(hasControl)).filter(
    (group: RichTextEditorControl[]) => group.length > 0
  )
}

/**
 * Maps StarterKit extension keys to toolbar controls.
 * - History: omitted on purpose so it is never disabled — Ctrl+Z/Ctrl+Y stay available; only undo/redo toolbar buttons are gated by controls.
 * - code/codeBlock: no toolbar controls in this editor; when controls is specified we set them to false, so pasting code blocks works only in full mode (controls undefined). Intentional.
 */
const STARTER_KIT_CONTROLS: {
  [K in keyof StarterKitOptions]?: RichTextEditorControl[]
} = {
  blockquote: ['blockquote'],
  bold: ['bold'],
  bulletList: ['bulletList'],
  code: undefined,
  codeBlock: undefined,
  heading: ['h1', 'h2', 'h3'],
  horizontalRule: ['hr'],
  italic: ['italic'],
  listItem: ['bulletList', 'orderedList'],
  orderedList: ['orderedList'],
  strike: ['strikethrough'],
}

export function buildStarterKitConfig(
  controls: RichTextEditorControl[] | undefined
): Partial<StarterKitOptions> | undefined {
  if (controls === undefined) return undefined
  const has = (c: RichTextEditorControl) => controls.includes(c)
  const config: Partial<StarterKitOptions> = {}
  for (const [key, controlList] of Object.entries(STARTER_KIT_CONTROLS) as [
    keyof StarterKitOptions,
    RichTextEditorControl[] | undefined
  ][]) {
    const enabled = controlList !== undefined && controlList.some(has)
    if (!enabled)
      (config as Partial<StarterKitOptions>)[key as keyof StarterKitOptions] =
        false
  }
  return config
}

const controlMap: Record<RichTextEditorControl, React.ComponentType> = {
  bold: Editor.Bold,
  italic: Editor.Italic,
  underline: Editor.Underline,
  strikethrough: Editor.Strikethrough,
  clearFormatting: Editor.ClearFormatting,
  highlight: Editor.Highlight,
  h1: Editor.H1,
  h2: Editor.H2,
  h3: Editor.H3,
  blockquote: Editor.Blockquote,
  hr: Editor.Hr,
  bulletList: Editor.BulletList,
  orderedList: Editor.OrderedList,
  link: Editor.Link,
  unlink: Editor.Unlink,
  undo: Editor.Undo,
  redo: Editor.Redo,
}

export function renderToolbarControl(
  control: RichTextEditorControl,
  key: string
): React.ReactElement {
  const Component = controlMap[control]
  return <Component key={key} />
}
